import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { initAI, isTelemetryReady, setCommonProps, trackPageView } from "@/telemetry/ai";
import { registerConsentState } from "@/telemetry/tracking";
import { useConsent } from "./useConsent";
import { ENV } from "@/config/env";

let telemetryInitialized = false;

export function TelemetryGate(): null {
  const consentState = useConsent();
  const location = useLocation();

  // Extract boolean for cleaner dependency
  const analyticsAllowed = consentState.isAllowed("analytics");

  // Effect 1: Initialize telemetry ONLY after consent is granted
  useEffect(() => {
    const telemetryConfigured = !!ENV.APPLICATIONINSIGHTS_CONNECTION_STRING;
    if (!telemetryConfigured || !analyticsAllowed) {
      return; // Wait for both config AND consent
    }

    // Don't re-initialize if already done
    if (telemetryInitialized && isTelemetryReady()) {
      return;
    }

    const initialized = initAI();
    if (!initialized) {
      return;
    }

    if (!telemetryInitialized) {
      setCommonProps();
      telemetryInitialized = true;
    }
  }, [analyticsAllowed]); // Simple boolean dependency, avoids function refs

  // Effect 2: Update consent state whenever it changes
  useEffect(() => {
    registerConsentState(consentState);
  }, [consentState]);

  // Effect 3: Track page views manually to capture accurate duration
  // (Runs after render/suspense, so duration includes component load time)
  useEffect(() => {
    if (analyticsAllowed && isTelemetryReady()) {
      trackPageView(location.pathname);
    }
  }, [location.pathname, analyticsAllowed]);

  return null;
}

export default TelemetryGate;
